import type { RequestConfig } from '@patsnap/common-ps-http'
import { inject, injectable } from '../shared/inversify'
import type psHttp from '@patsnap/common-ps-http';
import _ from 'lodash'
import type { AxiosError } from 'axios';
import { PsHttpChannel } from './http-channel';

/**
 * Represents the error response from the patsnap core data API.
 */
export interface CoreDataError {
    error_code : string;
    error_message: string;
    message_vars: string[];
    numeric_error_code : number;
    originating_service: string;
    timestamp: string;
}

/**
 * @deprecated
 */
export type MaybeError<T> = T & CoreDataError;

/**
 * Represents the response data for a PatSnap error.
 * @template T - The type of the error parameters.
 */
export type PatSnapErrorResponseData<T = any> = {
    data: CoreDataError;
    error_code: number;
    error_msg: string;
    error_params: T;
    status: false;
}

/**
 * Represents the success response data structure for PatSnap API.
 * @template T - The type of the data property.
 */
export interface PatSnapSuccessResponseData<T = unknown> {
    data: T;
    error_code: 0;
    status: true;
}

/**
 * Represents the response data for a PatSnap API request.
 * It can either be an error response or a success response.
 * @template T - The type of the response data.
 */
export type PatSnapCommonResponseData<T = any> = PatSnapSuccessResponseData<T> | PatSnapErrorResponseData<T>;

export const PsHttp = Symbol.for('psHttp')

export type PsHttp = typeof psHttp;

export function isPatSnapErrorResponseData(data: PatSnapCommonResponseData): data is PatSnapErrorResponseData {
    return data.status === false
}


/**
 * PsHttpServiceOptions interface represents the options for the PsHttpService.
 */
export interface PsHttpServiceOptions {
    headers?: Record<string, string>;
    baseURL?: string;
}

/**
 * Represents an error that occurs during an HTTP request in the PatSnap application.
 */
export class PsHttpError extends Error {
    readonly type = 'PsHttpError';
    readonly cause: PatSnapErrorResponseData;
    constructor(message?: string, options?: ErrorOptions) {
        super(message, options);
        this.cause = options?.cause as PatSnapErrorResponseData;
    }
}

/**
 * Checks if the given error is an instance of PsHttpError.
 * @param error - The error to check.
 * @returns True if the error is a PsHttpError, false otherwise.
 */
export function isPsHttpError(error: any): error is PsHttpError {
    if (!error) { return false; }
    return error && error.type === 'PsHttpError'
}

/**
 * PsHttpService is an abstract class that provides a base implementation for making HTTP requests.
 * It includes methods for performing various types of HTTP requests such as GET, POST, PUT, PATCH, and DELETE.
 * The class also handles error handling and response validation.
 */
@injectable()
export abstract class PsHttpService {
    abstract readonly options: PsHttpServiceOptions;

    constructor(
        @inject(PsHttp)
        protected readonly httpClient: PsHttp,
        @inject(PsHttpChannel)
        protected readonly httpChannelClient: PsHttpChannel,
    ) {

    }

    request<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config.headers = _.merge({}, this.options.headers, config.headers)
        config.baseURL = config.baseURL || this.options.baseURL
        return this.httpClient<T>(config)
            .then((res) => {
                const data = res.data
                if (isPatSnapErrorResponseData(data)) {
                    const error = new PsHttpError(data.data.error_message, {
                        cause: data,
                    });
                    throw error;
                }
                return res.data
            })
            .catch((error: AxiosError) => {
                throw error
            })
    }

    channel<T extends PatSnapCommonResponseData>(symbolId: symbol, config: RequestConfig): Promise<T> {
        config.headers = _.merge({}, this.options.headers, config.headers)
        config.baseURL = config.baseURL || this.options.baseURL

        const channel = this.httpChannelClient.getChannel<T, RequestConfig>(symbolId);

        return channel.requestAsPromise(config)
            .then((data) => {
                if (isPatSnapErrorResponseData(data)) {
                    const error = new PsHttpError(data.data.error_message, {
                        cause: data,
                    });
                    throw error;
                }
                return data
            })
            .catch((error: AxiosError) => {
                throw error
            })
    }

    get<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config = _.merge({
            method: 'get',
        }, config)
        return this.request<T>(config)
    }

    post<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config = _.merge({
            method: 'post',
        }, config)
        return this.request<T>(config)
    }

    put<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config = _.merge({
            method: 'put',
        }, config)
        return this.request<T>(config)
    }

    patch<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config = _.merge({
            method: 'patch',
        }, config)
        return this.request<T>(config)
    }


    delete<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config = _.merge({
            method: 'delete',
        }, config)
        return this.request<T>(config)
    }

    postForm<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config = _.merge({
            method: 'post',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
        }, config)
        return this.request<T>(config)
    }

    postMultipartForm<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config = _.merge({
            method: 'post',
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }, config)
        return this.request<T>(config)
    }

    patchMultipartForm<T extends PatSnapCommonResponseData>(config: RequestConfig): Promise<T> {
        config = _.merge({
            method: 'patch',
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }, config)
        return this.request<T>(config)
    }
}
